/**
 * Classification Debug Component
 * 
 * Add this component to your app to debug and test the classification system.
 * Usage: <ClassificationDebug />
 */

import { useState } from 'react';
import { getClassificationConfig, isClassificationAvailable, getConfigSummary } from '@/lib/classification-config';
import { getWorkflowIntegrationService } from '@/services/workflow-integration';
import { supabase } from '@/integrations/supabase/client';
import { QuickLoggerFactory } from '@/lib/universal-logger';
import { classificationDiagnostics } from '@/services/classification-diagnostics';
import { ProcessingOptimizer } from '@/services/processing-optimizer';

const databaseLogger = QuickLoggerFactory.database();

export function ClassificationDebug() {
  const [debugResult, setDebugResult] = useState<any>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [testPrompt, setTestPrompt] = useState("Write a story about a dragon");
  const [isLoading, setIsLoading] = useState(false);
  const [dbCheckResult, setDbCheckResult] = useState<any>(null);
  const [dbTestResult, setDbTestResult] = useState<any>(null);
  
  // New diagnostic states
  const [diagnosticReport, setDiagnosticReport] = useState<any>(null);
  const [healthCheck, setHealthCheck] = useState<any>(null);
  const [optimizerStatus, setOptimizerStatus] = useState<any>(null);
  const [rejectionTest, setRejectionTest] = useState<any>(null);
  const [isRunningDiagnostics, setIsRunningDiagnostics] = useState(false);
  
  // Mistral API diagnostic states
  const [mistralApiTest, setMistralApiTest] = useState<any>(null);
  const [isTesterMistralApi, setIsTestingMistralApi] = useState(false);
  
  // Classification service direct test states
  const [classificationServiceTest, setClassificationServiceTest] = useState<any>(null);
  const [isTestingClassificationService, setIsTestingClassificationService] = useState(false);

  const runDebugCheck = () => {
    try {
      const config = getClassificationConfig();
      const isAvailable = isClassificationAvailable();
      const summary = getConfigSummary();
      
      setDebugResult({
        config,
        isAvailable,
        summary,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setDebugResult({
        error: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString()
      });
    }
  };

  const testClassification = async () => {
    setIsLoading(true);
    try {
      const service = getWorkflowIntegrationService();
      const result = await service.triggerClassification('test-prompt-id', testPrompt);
      setTestResult({
        success: true,
        result,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  const testDatabaseUpdate = async () => {
    try {
      // First, get a recent prompt to test with
      const { data: recentPrompts, error: promptsError } = await supabase
        .from('stored_prompts')
        .select('id, prompt, prompt_category, created_at')
        .order('created_at', { ascending: false })
        .limit(3);

      if (promptsError) {
        throw new Error(`Failed to fetch recent prompts: ${promptsError.message}`);
      }

      if (!recentPrompts || recentPrompts.length === 0) {
        throw new Error('No prompts found in database');
      }

      const testPrompt = recentPrompts[0];
      databaseLogger.debug('Testing database update with prompt:', { 
        prompt: testPrompt,
        operation: 'classification_debug_test'
      });

      // Test the update query directly
      const testCategory = 'Test Category ' + Date.now();
      const { data: updateData, error: updateError } = await supabase
        .from('stored_prompts')
        .update({ prompt_category: testCategory })
        .eq('id', testPrompt.id)
        .select('id, prompt_category, updated_at');

      if (updateError) {
        throw new Error(`Update failed: ${updateError.message}`);
      }

      databaseLogger.debug('Update result:', { 
        updateData,
        operation: 'classification_debug_update'
      });

      // Verify the update worked
      const { data: verifyData, error: verifyError } = await supabase
        .from('stored_prompts')
        .select('id, prompt, prompt_category, created_at, updated_at')
        .eq('id', testPrompt.id)
        .single();

      if (verifyError) {
        throw new Error(`Verification failed: ${verifyError.message}`);
      }

      setDbTestResult({
        success: true,
        originalPrompt: testPrompt,
        testCategory,
        updateResult: updateData,
        verificationResult: verifyData,
        updateWorked: verifyData.prompt_category === testCategory,
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      setDbTestResult({
        success: false,
        error: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString()
      });
    }
  };

  const checkDatabase = async () => {
    try {
      // Check if prompt_category column exists
      const { data: columnData, error: columnError } = await supabase
        .from('stored_prompts')
        .select('id, prompt_category')
        .limit(1);

      // Check if audit_log table exists
      const { data: _auditData, error: auditError } = await supabase
        .from('audit_log')
        .select('id')
        .limit(1);

      // Check recent prompts and their categories
      const { data: recentPrompts, error: promptsError } = await supabase
        .from('stored_prompts')
        .select('id, prompt, prompt_category, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      // Check database schema for stored_prompts
      const { data: schemaData, error: schemaError } = await supabase
        .rpc('get_column_info', { table_name: 'stored_prompts' })
        .single();

      setDbCheckResult({
        promptCategoryColumn: {
          exists: !columnError,
          error: columnError?.message,
          sampleData: columnData?.slice(0, 3)
        },
        auditLogTable: {
          exists: !auditError,
          error: auditError?.message
        },
        recentPrompts: {
          data: recentPrompts,
          error: promptsError?.message
        },
        schema: {
          data: schemaData,
          error: schemaError?.message
        },
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setDbCheckResult({
        error: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString()
      });
    }
  };

  const createAuditTable = async () => {
    try {
      // This is the SQL from generateAuditLogTableSQL()
      const sql = `
        CREATE TABLE IF NOT EXISTS public.audit_log (
            id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
            event TEXT NOT NULL,
            category TEXT NOT NULL,
            severity TEXT NOT NULL,
            user_id UUID REFERENCES auth.users(id),
            ip_address TEXT,
            details JSONB NOT NULL DEFAULT '{}'::jsonb,
            created_at TIMESTAMPTZ NOT NULL DEFAULT now()
        );
        
        ALTER TABLE public.audit_log ENABLE ROW LEVEL SECURITY;
        
        CREATE POLICY IF NOT EXISTS "Users can view their own audit logs"
          ON public.audit_log
          FOR SELECT
          USING (auth.uid() = user_id);
        
        CREATE INDEX IF NOT EXISTS audit_log_user_id_idx ON public.audit_log (user_id);
        CREATE INDEX IF NOT EXISTS audit_log_created_at_idx ON public.audit_log (created_at);
        CREATE INDEX IF NOT EXISTS audit_log_category_idx ON public.audit_log (category);
        CREATE INDEX IF NOT EXISTS audit_log_severity_idx ON public.audit_log (severity);
      `;
      
      const { error } = await supabase.rpc('exec_sql', { sql_query: sql });
      
      if (error) {
        alert(`Error creating audit table: ${error.message}\n\nYou may need to run this SQL manually in Supabase SQL Editor:\n\n${sql}`);
      } else {
        alert('Audit table created successfully!');
        checkDatabase(); // Refresh the check
      }
    } catch (error) {
      alert(`Error: ${error instanceof Error ? error.message : String(error)}\n\nYou may need to create the audit_log table manually in Supabase.`);
    }
  };

  // New diagnostic functions
  const runComprehensiveDiagnostics = async () => {
    setIsRunningDiagnostics(true);
    try {
      const report = await classificationDiagnostics.generateDiagnosticReport();
      setDiagnosticReport({
        success: true,
        data: report,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setDiagnosticReport({
        success: false,
        error: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsRunningDiagnostics(false);
    }
  };

  const runQuickHealthCheck = async () => {
    try {
      const health = await classificationDiagnostics.getQuickHealthCheck();
      setHealthCheck({
        success: true,
        data: health,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setHealthCheck({
        success: false,
        error: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString()
      });
    }
  };

  const getOptimizerStatus = () => {
    try {
      const optimizer = ProcessingOptimizer.getInstance();
      const status = optimizer.getOptimizerStatus();
      setOptimizerStatus({
        success: true,
        data: {
          circuitBreakerState: status.circuitBreakerState,
          adaptiveThresholds: status.adaptiveThresholds,
          modeSuccessRates: Object.fromEntries(status.modeSuccessRates),
          performanceHistorySize: status.performanceHistorySize
        },
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setOptimizerStatus({
        success: false,
        error: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString()
      });
    }
  };

  const testPromptRejection = async () => {
    try {
      const testResult = await classificationDiagnostics.testPromptClassification(testPrompt);
      setRejectionTest({
        success: true,
        data: testResult,
        testPrompt,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setRejectionTest({
        success: false,
        error: error instanceof Error ? error.message : String(error),
        testPrompt,
        timestamp: new Date().toISOString()
      });
    }
  };

  const resetOptimizer = () => {
    try {
      classificationDiagnostics.resetOptimizer();
      alert('✅ Processing optimizer reset successfully!');
      // Refresh optimizer status
      getOptimizerStatus();
    } catch (error) {
      alert(`❌ Error resetting optimizer: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const forceCircuitBreakerClose = () => {
    try {
      classificationDiagnostics.forceCircuitBreakerClose();
      alert('✅ Circuit breaker forced closed!');
      // Refresh optimizer status
      getOptimizerStatus();
    } catch (error) {
      alert(`❌ Error forcing circuit breaker: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const temporarilyDisableRejections = () => {
    try {
      classificationDiagnostics.temporarilyDisableRejections();
      alert('⚠️ Rejections temporarily disabled! Remember to restore normal behavior when done debugging.');
      // Refresh optimizer status
      getOptimizerStatus();
    } catch (error) {
      alert(`❌ Error disabling rejections: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const restoreNormalRejections = () => {
    try {
      classificationDiagnostics.restoreNormalRejections();
      alert('✅ Normal rejection behavior restored!');
      // Refresh optimizer status
      getOptimizerStatus();
    } catch (error) {
      alert(`❌ Error restoring rejections: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // Mistral API diagnostic functions
  const testMistralApiConnectivity = async () => {
    setIsTestingMistralApi(true);
    try {
      // Check environment configuration
      const mistralApiKey = import.meta.env.VITE_MISTRAL_API_KEY;
      const classificationEnabled = import.meta.env.VITE_ENABLE_CLASSIFICATION;
      
      const configCheck = {
        hasApiKey: !!mistralApiKey,
        apiKeyLength: mistralApiKey ? mistralApiKey.length : 0,
        apiKeyMasked: mistralApiKey ? `${mistralApiKey.substring(0, 8)}...${mistralApiKey.substring(mistralApiKey.length - 4)}` : null,
        classificationEnabled: classificationEnabled === 'true',
        envVarPresent: !!classificationEnabled
      };

      if (!mistralApiKey) {
        setMistralApiTest({
          success: false,
          error: 'VITE_MISTRAL_API_KEY not found in environment variables',
          configCheck,
          timestamp: new Date().toISOString()
        });
        return;
      }

      // Test actual API call to Mistral
      databaseLogger.debug('Testing Mistral API connectivity', { 
        operation: 'mistral_api_connectivity_test',
        hasApiKey: configCheck.hasApiKey 
      });

      const testApiCall = async () => {
        const response = await fetch('https://api.mistral.ai/v1/models', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${mistralApiKey}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        return data;
      };

      const modelsResponse = await testApiCall();

      // Test a simple chat completion to verify classification endpoint access
      const testClassificationCall = async () => {
        const response = await fetch('https://api.mistral.ai/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${mistralApiKey}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: 'mistral-small-latest',
            messages: [
              { role: 'user', content: 'Test message for API connectivity' }
            ],
            max_tokens: 10,
            temperature: 0.1
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Chat completion failed: ${response.status} ${response.statusText} - ${errorText}`);
        }

        const data = await response.json();
        return data;
      };

      const chatResponse = await testClassificationCall();

      setMistralApiTest({
        success: true,
        configCheck,
        apiConnectivity: {
          modelsEndpoint: {
            success: true,
            modelCount: modelsResponse.data?.length || 0,
            availableModels: modelsResponse.data?.slice(0, 5).map((m: any) => m.id) || []
          },
          chatEndpoint: {
            success: true,
            responseReceived: !!chatResponse.choices,
            usage: chatResponse.usage
          }
        },
        recommendations: [
          '✅ Mistral API is accessible and working',
          '✅ Both models and chat endpoints are responding',
          'Classification service should be able to make API calls'
        ],
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      databaseLogger.error('Mistral API connectivity test failed', { 
        operation: 'mistral_api_connectivity_test',
        error: error instanceof Error ? error.message : String(error)
      });

      setMistralApiTest({
        success: false,
        error: error instanceof Error ? error.message : String(error),
        configCheck: {
          hasApiKey: !!import.meta.env.VITE_MISTRAL_API_KEY,
          apiKeyLength: import.meta.env.VITE_MISTRAL_API_KEY?.length || 0,
          classificationEnabled: import.meta.env.VITE_ENABLE_CLASSIFICATION === 'true'
        },
        recommendations: [
          '❌ Mistral API connectivity failed',
          '🔍 Check API key validity and permissions',
          '🔍 Verify network connectivity',
          '🔍 Check if API endpoint is accessible'
        ],
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsTestingMistralApi(false);
    }
  };

  const testClassificationServiceDirectly = async () => {
    setIsTestingClassificationService(true);
    try {
      // Import services dynamically to avoid circular dependencies
      const { PromptClassificationService } = await import('@/services/prompt-classification-service');
      const { MistralApiService } = await import('@/services/mistral-api-service');
      const { MistralConfigFactory } = await import('@/services/mistral-config-factory');
      
      databaseLogger.debug('Testing classification service directly', { 
        operation: 'classification_service_direct_test'
      }, undefined);

      // Create the required dependencies
      let mistralConfig;
      try {
        // Try to create config from environment
        mistralConfig = MistralConfigFactory.createFromEnvironment({ 
          requireApiKey: true,
          requireHttps: false // Allow HTTP for testing
        });
      } catch (configError) {
        // Fall back to test config
        const apiKey = import.meta.env.VITE_MISTRAL_API_KEY;
        if (!apiKey) {
          throw new Error('VITE_MISTRAL_API_KEY environment variable is required for testing');
        }
        mistralConfig = MistralConfigFactory.createTestConfig(apiKey);
      }
      
      // Create MistralApiService
      const mistralApiService = new MistralApiService(mistralConfig);
      
      // Test API connectivity first
      const isConnected = await mistralApiService.testConnection();
      if (!isConnected) {
        throw new Error('Failed to connect to Mistral API');
      }
      
      // Create classification service with proper dependencies
      const service = new PromptClassificationService({}, {
        mistralApiService
      });
      
      // Test with the same prompt that's been failing
      const promptToTest = testPrompt || "Write a Python function to calculate factorial";
      const result = await service.classifyPrompt('test-classification-prompt', promptToTest, {});

      setClassificationServiceTest({
        success: true,
        timestamp: new Date().toISOString(),
        result: {
          serviceCall: '✅ Success',
          requestMade: '✅ Yes',
          response: result,
          details: `Successfully classified prompt with category: ${result.categoryName}`,
          apiConnection: '✅ Connected',
          processingMode: result.processingMode,
          processingTime: `${result.processingTimeMs}ms`
        }
      });

    } catch (error) {
      databaseLogger.error('Direct classification service test failed', { 
        operation: 'classification_service_direct_test',
        error: error instanceof Error ? error.message : String(error)
      });

      setClassificationServiceTest({
        success: false,
        timestamp: new Date().toISOString(),
        result: {
          serviceCall: '❌ Failed',
          requestMade: '❌ No',
          error: error instanceof Error ? error.message : String(error),
          details: 'Service instantiation or call failed',
          troubleshooting: error instanceof Error && error.message.includes('VITE_MISTRAL_API_KEY') 
            ? 'Check that VITE_MISTRAL_API_KEY is set in your .env file'
            : 'Check browser console for detailed error information'
        }
      });
    } finally {
      setIsTestingClassificationService(false);
    }
  };

  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg space-y-4">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
        🔍 Classification System Debug Panel
      </h2>
      
      <div className="grid gap-4 md:grid-cols-2">
        {/* Configuration Check */}
        <div className="space-y-2">
          <button
            onClick={runDebugCheck}
            className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            🔧 Check Configuration
          </button>
          
          {debugResult && (
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded text-sm">
              <strong>Config Result:</strong>
              <pre className="mt-1 text-xs overflow-auto">
                {JSON.stringify(debugResult, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Database Check */}
        <div className="space-y-2">
          <button
            onClick={checkDatabase}
            className="w-full px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
          >
            🗄️ Check Database Tables
          </button>
          
          {dbCheckResult && (
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded text-sm">
              <strong>Database Status:</strong>
              <pre className="mt-1 text-xs overflow-auto">
                {JSON.stringify(dbCheckResult, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>

      {/* Database Update Test */}
      <div className="space-y-2">
        <button
          onClick={testDatabaseUpdate}
          className="w-full px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
        >
          🧪 Test Database Update Directly
        </button>
        
        {dbTestResult && (
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded text-sm">
            <strong>Database Test Result:</strong>
            <pre className="mt-1 text-xs overflow-auto">
              {JSON.stringify(dbTestResult, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Test Classification */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <input
            type="text"
            value={testPrompt}
            onChange={(e) => setTestPrompt(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Enter test prompt"
          />
          <button
            onClick={testClassification}
            disabled={isLoading}
            className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50 transition-colors"
          >
            {isLoading ? '⏳ Testing...' : '🧪 Test Classification'}
          </button>
        </div>
        
        {testResult && (
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded text-sm">
            <strong>Test Result:</strong>
            <pre className="mt-1 text-xs overflow-auto">
              {JSON.stringify(testResult, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Mistral API Connectivity Tests */}
      <div className="border-t pt-4 space-y-4">
        <h3 className="font-semibold text-gray-900 dark:text-white">🌐 Mistral API Connectivity:</h3>
        
        <div className="grid gap-2 md:grid-cols-2">
          <button
            onClick={testMistralApiConnectivity}
            disabled={isTesterMistralApi}
            className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors text-sm disabled:opacity-50"
          >
            {isTesterMistralApi ? '🔄 Testing API...' : '🌐 Test Mistral API'}
          </button>
          
          <button
            onClick={testClassificationServiceDirectly}
            disabled={isTestingClassificationService}
            className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors text-sm disabled:opacity-50"
          >
            {isTestingClassificationService ? '🔄 Testing Service...' : '🧪 Test Classification Service'}
          </button>
        </div>

        {mistralApiTest && (
          <div className={`p-4 rounded-lg border ${mistralApiTest.success ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={mistralApiTest.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                {mistralApiTest.success ? '✅' : '❌'}
              </span>
              <strong className={mistralApiTest.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}>
                Mistral API Test {mistralApiTest.success ? 'Passed' : 'Failed'}
              </strong>
              <span className="text-xs text-gray-500">({mistralApiTest.timestamp})</span>
            </div>
            
            {mistralApiTest.configCheck && (
              <div className="mb-3">
                <strong className="text-sm">Configuration:</strong>
                <ul className="text-xs mt-1 space-y-1">
                  <li>API Key: {mistralApiTest.configCheck.hasApiKey ? `✅ Present (${mistralApiTest.configCheck.apiKeyLength} chars)` : '❌ Missing'}</li>
                  {mistralApiTest.configCheck.apiKeyMasked && (
                    <li>Key Preview: <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{mistralApiTest.configCheck.apiKeyMasked}</code></li>
                  )}
                  <li>Classification Enabled: {mistralApiTest.configCheck.classificationEnabled ? '✅ Yes' : '❌ No'}</li>
                </ul>
              </div>
            )}

            {mistralApiTest.apiConnectivity && (
              <div className="mb-3">
                <strong className="text-sm">API Connectivity:</strong>
                <ul className="text-xs mt-1 space-y-1">
                  <li>Models Endpoint: {mistralApiTest.apiConnectivity.modelsEndpoint.success ? `✅ ${mistralApiTest.apiConnectivity.modelsEndpoint.modelCount} models available` : '❌ Failed'}</li>
                  {mistralApiTest.apiConnectivity.modelsEndpoint.availableModels?.length > 0 && (
                    <li>Available Models: <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded text-xs">{mistralApiTest.apiConnectivity.modelsEndpoint.availableModels.join(', ')}</code></li>
                  )}
                  <li>Chat Endpoint: {mistralApiTest.apiConnectivity.chatEndpoint.success ? '✅ Working' : '❌ Failed'}</li>
                  {mistralApiTest.apiConnectivity.chatEndpoint.usage && (
                    <li>Usage: <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded text-xs">{JSON.stringify(mistralApiTest.apiConnectivity.chatEndpoint.usage)}</code></li>
                  )}
                </ul>
              </div>
            )}

            {mistralApiTest.directServiceTest && (
              <div className="mb-3">
                <strong className="text-sm">Direct Service Test:</strong>
                <ul className="text-xs mt-1 space-y-1">
                  <li>Service Call: {mistralApiTest.directServiceTest.success ? '✅ Success' : '❌ Failed'}</li>
                  <li>Request Made: {mistralApiTest.directServiceTest.requestMade ? '✅ Yes' : '❌ No'}</li>
                  {mistralApiTest.directServiceTest.result && (
                    <li>Result: <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded text-xs">{JSON.stringify(mistralApiTest.directServiceTest.result).substring(0, 100)}...</code></li>
                  )}
                  {mistralApiTest.directServiceTest.error && (
                    <li>Error: <code className="bg-red-100 dark:bg-red-900 px-1 rounded text-xs">{mistralApiTest.directServiceTest.error}</code></li>
                  )}
                </ul>
              </div>
            )}

            {mistralApiTest.error && (
              <div className="mb-3">
                <strong className="text-sm text-red-600 dark:text-red-400">Error:</strong>
                <code className="block text-xs bg-red-100 dark:bg-red-900/50 p-2 rounded mt-1 break-all">
                  {mistralApiTest.error}
                </code>
              </div>
            )}

            {mistralApiTest.recommendations && (
              <div>
                <strong className="text-sm">Recommendations:</strong>
                <ul className="text-xs mt-1 space-y-1">
                  {mistralApiTest.recommendations.map((rec: string, idx: number) => (
                    <li key={idx}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Classification Service Direct Test Results */}
        {classificationServiceTest && (
          <div className={`p-4 rounded-lg border ${classificationServiceTest.success ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={classificationServiceTest.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                {classificationServiceTest.success ? '✅' : '❌'}
              </span>
              <strong className={classificationServiceTest.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}>
                Classification Service Test {classificationServiceTest.success ? 'Passed' : 'Failed'}
              </strong>
              <span className="text-xs text-gray-500">({classificationServiceTest.timestamp})</span>
            </div>
            
            <div className="text-sm space-y-2">
              <div><strong>Service Call:</strong> {classificationServiceTest.result.serviceCall}</div>
              <div><strong>Request Made:</strong> {classificationServiceTest.result.requestMade}</div>
              {classificationServiceTest.result.apiConnection && (
                <div><strong>API Connection:</strong> {classificationServiceTest.result.apiConnection}</div>
              )}
              {classificationServiceTest.result.processingMode && (
                <div><strong>Processing Mode:</strong> {classificationServiceTest.result.processingMode}</div>
              )}
              {classificationServiceTest.result.processingTime && (
                <div><strong>Processing Time:</strong> {classificationServiceTest.result.processingTime}</div>
              )}
              {classificationServiceTest.result.details && (
                <div><strong>Details:</strong> {classificationServiceTest.result.details}</div>
              )}
              {classificationServiceTest.result.error && (
                <div className="text-red-600 dark:text-red-400">
                  <strong>Error:</strong> <code className="bg-red-100 dark:bg-red-900 px-1 rounded text-xs">{classificationServiceTest.result.error}</code>
                </div>
              )}
              {classificationServiceTest.result.troubleshooting && (
                <div className="text-orange-600 dark:text-orange-400">
                  <strong>💡 Troubleshooting:</strong> {classificationServiceTest.result.troubleshooting}
                </div>
              )}
              {classificationServiceTest.result.response && (
                <details className="text-xs">
                  <summary className="cursor-pointer font-medium">View Full Response</summary>
                  <pre className="mt-1 overflow-auto bg-gray-100 dark:bg-gray-800 p-2 rounded">
                    {JSON.stringify(classificationServiceTest.result.response, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Advanced Diagnostics */}
      <div className="border-t pt-4 space-y-4">
        <h3 className="font-semibold text-gray-900 dark:text-white">🚀 Advanced System Diagnostics</h3>
        
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* Comprehensive Diagnostics */}
          <div className="space-y-2">
            <button
              onClick={runComprehensiveDiagnostics}
              disabled={isRunningDiagnostics}
              className="w-full px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:opacity-50 transition-colors"
            >
              {isRunningDiagnostics ? '⏳ Running...' : '📊 Full System Report'}
            </button>
            
            {diagnosticReport && (
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                <strong>Comprehensive Report:</strong>
                {diagnosticReport.success ? (
                  <div className="mt-2 space-y-2">
                    <div className="text-xs">
                      <strong>Status:</strong> {diagnosticReport.data.systemStatus.circuitBreakerState}
                    </div>
                    <div className="text-xs">
                      <strong>Issues:</strong> {diagnosticReport.data.potentialIssues.length}
                    </div>
                    <div className="text-xs">
                      <strong>Success Rate:</strong> {(diagnosticReport.data.performanceStats.successRate * 100).toFixed(1)}%
                    </div>
                    <div className="text-xs">
                      <strong>Resource Health:</strong> {(diagnosticReport.data.decisionFactors.resourceHealth * 100).toFixed(1)}%
                    </div>
                    {diagnosticReport.data.potentialIssues.length > 0 && (
                      <div className="text-xs text-red-600 dark:text-red-400">
                        <strong>Issues:</strong>
                        <ul className="mt-1 ml-4">
                          {diagnosticReport.data.potentialIssues.slice(0, 3).map((issue: string, i: number) => (
                            <li key={i} className="list-disc">{issue}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <details className="text-xs">
                      <summary className="cursor-pointer font-medium">View Full Report</summary>
                      <pre className="mt-1 overflow-auto">
                        {JSON.stringify(diagnosticReport.data, null, 2)}
                      </pre>
                    </details>
                  </div>
                ) : (
                  <pre className="mt-1 text-xs overflow-auto text-red-600">
                    Error: {diagnosticReport.error}
                  </pre>
                )}
              </div>
            )}
          </div>

          {/* Quick Health Check */}
          <div className="space-y-2">
            <button
              onClick={runQuickHealthCheck}
              className="w-full px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
            >
              💓 Quick Health Check
            </button>
            
            {healthCheck && (
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                <strong>Health Status:</strong>
                {healthCheck.success ? (
                  <div className="mt-2 space-y-1">
                    <div className={`text-xs font-medium ${
                      healthCheck.data.status === 'healthy' ? 'text-green-600' :
                      healthCheck.data.status === 'degraded' ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      Status: {healthCheck.data.status.toUpperCase()}
                    </div>
                    <div className="text-xs">
                      Circuit Breaker: {healthCheck.data.circuitBreakerOpen ? '🔴 OPEN' : '🟢 CLOSED'}
                    </div>
                    <div className="text-xs">
                      Success Rate: {(healthCheck.data.successRate * 100).toFixed(1)}%
                    </div>
                    <div className="text-xs">
                      Avg Latency: {healthCheck.data.avgLatency.toFixed(0)}ms
                    </div>
                    {healthCheck.data.issues.length > 0 && (
                      <div className="text-xs text-red-600 dark:text-red-400">
                        Issues: {healthCheck.data.issues.join(', ')}
                      </div>
                    )}
                  </div>
                ) : (
                  <pre className="mt-1 text-xs overflow-auto text-red-600">
                    Error: {healthCheck.error}
                  </pre>
                )}
              </div>
            )}
          </div>

          {/* Optimizer Status */}
          <div className="space-y-2">
            <button
              onClick={getOptimizerStatus}
              className="w-full px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
            >
              ⚙️ Optimizer Status
            </button>
            
            {optimizerStatus && (
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                <strong>Processing Optimizer:</strong>
                {optimizerStatus.success ? (
                  <div className="mt-2 space-y-1">
                    <div className="text-xs">
                      Circuit Breaker: <span className={
                        optimizerStatus.data.circuitBreakerState === 'closed' ? 'text-green-600' : 'text-red-600'
                      }>
                        {optimizerStatus.data.circuitBreakerState.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-xs">
                      History Size: {optimizerStatus.data.performanceHistorySize}
                    </div>
                    <div className="text-xs">
                      Sync Timeout: {optimizerStatus.data.adaptiveThresholds.syncTimeoutThreshold}ms
                    </div>
                    <details className="text-xs">
                      <summary className="cursor-pointer font-medium">View Details</summary>
                      <pre className="mt-1 overflow-auto">
                        {JSON.stringify(optimizerStatus.data, null, 2)}
                      </pre>
                    </details>
                  </div>
                ) : (
                  <pre className="mt-1 text-xs overflow-auto text-red-600">
                    Error: {optimizerStatus.error}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Rejection Testing */}
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={testPrompt}
              onChange={(e) => setTestPrompt(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Test prompt for rejection analysis"
            />
            <button
              onClick={testPromptRejection}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              🔍 Test Rejection
            </button>
          </div>
          
          {rejectionTest && (
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded text-sm">
              <strong>Rejection Analysis:</strong>
              {rejectionTest.success ? (
                <div className="mt-2 space-y-1">
                  <div className={`text-xs font-medium ${
                    rejectionTest.data.wouldBeRejected ? 'text-red-600' : 'text-green-600'
                  }`}>
                    Would be rejected: {rejectionTest.data.wouldBeRejected ? 'YES' : 'NO'}
                  </div>
                  <div className="text-xs">
                    Recommended mode: {rejectionTest.data.recommendation.mode}
                  </div>
                  <div className="text-xs">
                    Confidence: {(rejectionTest.data.recommendation.confidence * 100).toFixed(1)}%
                  </div>
                  {rejectionTest.data.rejectionReason && (
                    <div className="text-xs text-red-600 dark:text-red-400">
                      Reason: {rejectionTest.data.rejectionReason}
                    </div>
                  )}
                  <details className="text-xs">
                    <summary className="cursor-pointer font-medium">View Decision Factors</summary>
                    <pre className="mt-1 overflow-auto">
                      {JSON.stringify(rejectionTest.data.recommendation.factors, null, 2)}
                    </pre>
                  </details>
                </div>
              ) : (
                <pre className="mt-1 text-xs overflow-auto text-red-600">
                  Error: {rejectionTest.error}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Recovery Actions */}
      <div className="border-t pt-4 space-y-2">
        <h3 className="font-semibold text-gray-900 dark:text-white">🚨 Recovery Actions</h3>
        
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-4">
          <button
            onClick={resetOptimizer}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
          >
            🔄 Reset Optimizer
          </button>
          
          <button
            onClick={forceCircuitBreakerClose}
            className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors text-sm"
          >
            🔧 Force Circuit Close
          </button>
          
          <button
            onClick={temporarilyDisableRejections}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-sm"
          >
            ⚠️ Disable Rejections
          </button>
          
          <button
            onClick={restoreNormalRejections}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-sm"
          >
            ✅ Restore Normal
          </button>
        </div>
        
        <div className="text-xs text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded">
          ⚠️ <strong>Warning:</strong> Recovery actions affect the live system. Use "Disable Rejections" only for debugging and remember to "Restore Normal" when done.
        </div>
      </div>

      {/* Quick Fixes */}
      <div className="border-t pt-4 space-y-2">
        <h3 className="font-semibold text-gray-900 dark:text-white">🔧 Quick Fixes:</h3>
        
        <div className="grid gap-2 md:grid-cols-2">
          <button
            onClick={createAuditTable}
            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors text-sm"
          >
            📋 Create audit_log Table
          </button>
          
          <button
            onClick={() => {
              const currentUrl = new URL(window.location.href);
              currentUrl.searchParams.set('disable_classification', 'true');
              alert(`To temporarily disable classification, add this to your .env file:\nVITE_ENABLE_CLASSIFICATION=false\n\nOr visit: ${currentUrl.href}`);
            }}
            className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors text-sm"
          >
            🚫 Disable Classification (Temp)
          </button>
        </div>
      </div>

      {/* Setup Instructions */}
      <div className="text-sm text-gray-600 dark:text-gray-400 border-t pt-4">
        <h3 className="font-semibold mb-2">📋 Setup Checklist:</h3>
        <ol className="list-decimal list-inside space-y-1">
          <li>✅ Add <code>VITE_ENABLE_CLASSIFICATION=true</code> to your .env file</li>
          <li>✅ Add <code>VITE_MISTRAL_API_KEY=your_key</code> to your .env file</li>
          <li>⚠️ Create audit_log table in Supabase (click button above)</li>
          <li>⚠️ Verify prompt_category column exists in stored_prompts table</li>
          <li>🔄 Restart dev server: <code>bun run dev</code></li>
        </ol>
        
        <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
          <strong>🚀 New Diagnostic Features:</strong>
          <ul className="mt-1 text-xs space-y-1">
            <li>• <strong>Full System Report:</strong> Comprehensive analysis of performance, errors, and system health</li>
            <li>• <strong>Quick Health Check:</strong> Instant status check with color-coded health indicators</li>
            <li>• <strong>Optimizer Status:</strong> Circuit breaker state, adaptive thresholds, and performance history</li>
            <li>• <strong>Rejection Analysis:</strong> Test why specific prompts are being rejected with detailed factors</li>
            <li>• <strong>Recovery Actions:</strong> Reset optimizer, force circuit breaker, disable/restore rejections</li>
          </ul>
        </div>
        
        <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
          <strong>🐛 Known Issues & Solutions:</strong>
          <ul className="mt-1 text-xs space-y-1">
            <li>• <strong>Classification rejections:</strong> Use "Test Rejection" to see why prompts are rejected</li>
            <li>• <strong>High system load:</strong> Check "Full System Report" for resource health metrics</li>
            <li>• <strong>Circuit breaker open:</strong> Use "Force Circuit Close" or "Reset Optimizer"</li>
            <li>• <strong>Performance issues:</strong> Review "Quick Health Check" for latency and success rates</li>
            <li>• <strong>For debugging only:</strong> Use "Disable Rejections" temporarily, then "Restore Normal"</li>
          </ul>
        </div>
      </div>
    </div>
  );
} 